import { useEffect, useRef } from 'react';
import { imageUrl } from '../lib/images';

export default function Lightbox({
  images = [],
  currentIndex = 0,
  isOpen = false,
  onClose,
  onNavigate,
}) {
  const touchStartX = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      } else if (e.key === 'ArrowLeft') {
        if (currentIndex > 0) onNavigate?.(currentIndex - 1);
        else onNavigate?.(images.length - 1);
      } else if (e.key === 'ArrowRight') {
        if (currentIndex < images.length - 1) onNavigate?.(currentIndex + 1);
        else onNavigate?.(0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, currentIndex, images.length, onClose, onNavigate]);

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        // Swiped left -> next
        if (currentIndex < images.length - 1) onNavigate?.(currentIndex + 1);
        else onNavigate?.(0);
      } else {
        // Swiped right -> prev
        if (currentIndex > 0) onNavigate?.(currentIndex - 1);
        else onNavigate?.(images.length - 1);
      }
    }
    touchStartX.current = null;
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    if (currentIndex > 0) onNavigate?.(currentIndex - 1);
    else onNavigate?.(images.length - 1);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    if (currentIndex < images.length - 1) onNavigate?.(currentIndex + 1);
    else onNavigate?.(0);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning i fuld skærm"
      className="fixed inset-0 z-50 bg-[#070708]/95 backdrop-blur-xl flex flex-col justify-between items-center p-4 md:p-8 animate-in fade-in duration-200"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Top bar: counter & close */}
      <div
        className="w-full flex items-center justify-between z-10 max-w-7xl mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs uppercase tracking-widest text-zinc-400 font-medium bg-black/40 px-3.5 py-1.5 rounded-none border border-white/10">
          Billede {currentIndex + 1} af {images.length}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-3 rounded-none bg-white/10 hover:bg-white/20 text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#B89B72]"
          aria-label="Luk fuldskærmsvisning"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
        </button>
      </div>

      {/* Main Image container with Prev / Next buttons */}
      <div
        className="relative flex-1 w-full max-w-6xl flex items-center justify-center my-4 select-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Prev button */}
        <button
          type="button"
          onClick={handlePrev}
          className="absolute left-2 md:left-4 z-20 p-3.5 rounded-none bg-black/60 hover:bg-[#B89B72] text-white hover:text-[#0C0C0D] transition-colors focus:outline-none focus:ring-2 focus:ring-[#B89B72] border border-white/10"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-base" aria-hidden="true" />
        </button>

        {/* The active image */}
        <img
          src={imageUrl(typeof currentImage === 'string' ? currentImage : (currentImage?.src || currentImage), 'width=1800,quality=90,format=auto')}
          alt={typeof currentImage === 'object' && currentImage?.alt ? currentImage.alt : `Lunde Media galleribillede ${currentIndex + 1}`}
          className="max-h-[75vh] md:max-h-[82vh] max-w-full object-contain rounded-none shadow-2xl transition-opacity duration-300 pointer-events-auto"
          loading="eager"
        />

        {/* Next button */}
        <button
          type="button"
          onClick={handleNext}
          className="absolute right-2 md:right-4 z-20 p-3.5 rounded-none bg-black/60 hover:bg-[#B89B72] text-white hover:text-[#0C0C0D] transition-colors focus:outline-none focus:ring-2 focus:ring-[#B89B72] border border-white/10"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-base" aria-hidden="true" />
        </button>
      </div>

      {/* Bottom Hint */}
      <div className="text-zinc-500 text-xs hidden md:block select-none">
        Brug venstre/højre piletaster for at navigere · Tryk Esc for at lukke
      </div>
    </div>
  );
}
